import { Checkbox, Combobox, Stack, Text, useCombobox } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { twMerge } from "tailwind-merge";
import { useCallback, useMemo } from "react";

import {
  ScreenersQueryKeys,
  type ScreenersTableQueryParams,
} from "@/components/table/views/screeners/screeners-columns";
import { ComboboxTargetButton } from "@/components/ui/combobox";
import { useFilterAnalytics } from "@/hooks/analytics.hooks";
import type { FilterParamsHandle } from "@/hooks/use-filter-params";
import type { UserListUser } from "@/types/user";

interface CreatedByFilterProps {
  filters: FilterParamsHandle<ScreenersTableQueryParams>;
  users: UserListUser[];
}

export const CreatedByFilter = ({ filters, users }: CreatedByFilterProps) => {
  const combobox = useCombobox();
  const trackFilter = useFilterAnalytics("Created by");

  const sortedUsersByEmail = useMemo(() => {
    return users.toSorted((a, b) => a.email.localeCompare(b.email));
  }, [users]);

  const selectedUserEmails = useMemo(
    () =>
      users
        .reduce(
          (emails, { email, id }) =>
            filters.has(ScreenersQueryKeys.userId, id)
              ? emails.concat(email)
              : emails,
          [] as string[],
        )
        .join(", "),
    [filters, users],
  );

  const renderOption = useCallback(
    (
      label: string,
      value: string,
      fullName: string | undefined,
      isLastOption = false,
    ) => {
      return (
        <Combobox.Option
          className={twMerge([
            "flex",
            "items-center",
            !isLastOption && "border-b",
            "w-full",
          ])}
          key={value}
          p={12}
          value={value}
        >
          <Checkbox
            checked={filters.has(ScreenersQueryKeys.userId, value)}
            mr={8}
            readOnly
          />
          <Stack gap={0} justify="center" align="flex-start">
            <Text size="sm">{label}</Text>
            {fullName && (
              <Text size="xs" className="text-uw-gray-5">
                {fullName}
              </Text>
            )}
          </Stack>
        </Combobox.Option>
      );
    },
    [filters],
  );

  return (
    <Combobox
      offset={6}
      onOptionSubmit={(value) => {
        trackFilter(value);
        filters.toggleValue("userId", value);
      }}
      position="bottom-start"
      radius={6}
      store={combobox}
      transitionProps={{ duration: 300, transition: "pop-top-left" }}
      width="max-content"
    >
      <ComboboxTargetButton
        leftSection={<FontAwesomeIcon className="text-sm" icon={faUser} />}
        onClear={() => filters.delete(ScreenersQueryKeys.userId)}
        showClearButton={filters.has(ScreenersQueryKeys.userId)}
        store={combobox}
      >
        {selectedUserEmails ? (
          <Text
            className={twMerge([
              "overflow-ellipsis",
              "overflow-hidden",
              "text-uw-gray-9",
              "w-[120px]",
              "whitespace-nowrap",
            ])}
            size="sm"
            ta="left"
          >
            {selectedUserEmails}
          </Text>
        ) : (
          "Select..."
        )}
      </ComboboxTargetButton>
      <Combobox.Dropdown className="overflow-y-auto" mah={330} p={0}>
        <Combobox.Options>
          {sortedUsersByEmail.map((user) =>
            renderOption(user.email, user.id, user.fullName),
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
